/* eslint-disable i18next/no-literal-string */
import React, { useEffect } from 'react';
import { memoizeFunction } from '@fluentui/react';
import { useAsync } from '@fluentui/react-hooks';
import { useNotifications } from 'store/layout/hooks/useNotifications';
import { INotification } from 'store/layout/layoutState';

import { Snackbar, SnackbarType } from '../Snackbar';

interface IGlobalToastProps {
  toast: INotification;
}

export const defaultDismissDelayInMs = 3000;
const fadeInAnimationDelay = 500;

const getSnackbarStyles = memoizeFunction((opacity: number) => ({
  root: { transition: `opacity ${fadeInAnimationDelay}ms`, opacity },
}));

/**
 * A {@link Snackbar} that will render in a React portal on the document body,
 * which will position it at the bottom of the viewport.
 *
 * @param props IGlobalToastProps
 * @returns {React.ReactElement} An instance of GlobalToast
 */
export const GlobalToast = (props: IGlobalToastProps) => {
  const { toast } = props;
  const { dismissDelayInMs = defaultDismissDelayInMs, id, message, messageType = SnackbarType.info, actions } = toast;
  const { dismissNotification } = useNotifications();
  const dismissAsync = useAsync();
  const animationAsync = useAsync();

  const [opacity, setOpacity] = React.useState(0);
  const styles = getSnackbarStyles(opacity);

  // make it manually dismissable if automatic dismiss is disabled
  const dismissable = toast.dismissable || dismissDelayInMs === 0;

  const onDismiss = React.useCallback(() => {
    // fade out before dismissal
    setOpacity(0);
    animationAsync.setTimeout(() => {
      dismissNotification(id);
    }, fadeInAnimationDelay);
  }, [animationAsync, dismissNotification, id]);

  useEffect(() => {
    if (dismissDelayInMs > 0) {
      dismissAsync.setTimeout(onDismiss, dismissDelayInMs + fadeInAnimationDelay * 2);
    }
  }, [dismissAsync, onDismiss, dismissDelayInMs]);

  useEffect(() => {
    // fade in after mount
    setOpacity(1);
  }, []);

  return (
    <Snackbar
      styles={styles}
      onDismiss={dismissable ? onDismiss : undefined}
      message={message}
      messageType={messageType}
      actions={actions}
    />
  );
};
